import React from "react";
import { useParams, Link, useLocation, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Wifi,
  WifiOff,
  Zap,
  Thermometer,
  Radio,
  Settings,
  Send,
  MapPin,
  Building,
  ScrollText,
  Pause,
  Play,
  SlidersHorizontal,
} from "@/components/icons";

import {
  fetchUnit,
  unitHeartbeat,
  permitCharging,
  stopSessionCharging,
  resumeSessionCharging,
  patchUnitSettings,
  fetchUnitBillingTotals,
} from "@/lib/api";
import { useAuth } from "@/app/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TouEditor } from "@/views/TouEditor";
import { TariffEditor } from "@/views/TariffEditor";
import { AppliedPricingProfiles } from "@/components/AppliedPricingProfiles";
import { PaymentQrCode } from "@/components/PaymentQrCode";
import { SessionHistory } from "@/views/SessionHistory";
import { UnitAssignment } from "@/views/UnitAssignment";
import { UnitTelemetryPanel } from "@/components/telemetry/UnitTelemetryPanel";
import {Switch} from "@/components/ui/switch";
import {ToggleGroup, ToggleGroupItem} from "@/components/ui/toggle-group";

function formatRelativeTime(isoStr: string | null | undefined): string {
  if (!isoStr) return "Never";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "Never";
  const diffMs = Date.now() - d.getTime();
  const diffS = Math.floor(diffMs / 1000);
  // Treat the near-zero window as "just now": this absorbs tiny clock skew
  // between browser and server (small negatives) and very recent pings.
  // Deliberately a SMALL window — a large negative means something is genuinely
  // wrong (it's the symptom of the Task 18 timezone bug) and must stay visible
  // rather than be masked as "just now", so a future regression can't hide.
  if (diffS > -15 && diffS < 15) return "just now";
  if (diffS < 60) return `${diffS}s ago`;
  const diffM = Math.floor(diffS / 60);
  if (diffM < 60) return `${diffM}m ago`;
  const diffH = Math.floor(diffM / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d ago`;
}

interface BackState {
  backTo?: string;
  backLabel?: string;
}

export function UnitDetailPage() {
  const { unitId } = useParams();
  const id = unitId ? decodeURIComponent(unitId) : "";
  const { isAdmin, isOrgUser } = useAuth();
  // Tabs live in the URL so an alert or a ticket can deep-link straight into the
  // telemetry view (with its range and filters). The telemetry panel is mounted
  // ONLY while its tab is open — every query it makes is a paid Athena scan, and
  // opening a charger must cost nothing.
  const [searchParams, setSearchParams] = useSearchParams();
  // Platform admins only — the lake is a support tool, not a tenant view, and
  // every panel it opens is a metered Athena scan. Org managers and drivers do
  // not get the tab, and `?tab=telemetry` in a shared link falls back to the
  // overview for them rather than mounting the panel. The API says the same
  // (web/telemetry_routes.py) — this is the affordance, not the gate.
  const canReadTelemetry = isAdmin;
  const tab =
    canReadTelemetry && searchParams.get("tab") === "telemetry" ? "telemetry" : "overview";
  const navState = useLocation().state as BackState | null;
  const backTo = navState?.backTo ?? "/";
  const backLabel = navState?.backLabel ? `Back to ${navState.backLabel}` : "Back";

  /* ---- Queries ---- */
  const unitQ = useQuery({
    queryKey: ["unit", id],
    queryFn: () => fetchUnit(id),
    enabled: !!id,
    refetchInterval: 5_000,
  });

  const billingQ = useQuery({
    queryKey: ["unit", id, "billing", "totals"],
    queryFn: () => fetchUnitBillingTotals(id),
    enabled: !!id && isAdmin,
    refetchInterval: 30_000,
  });

  /* ---- Mutations ---- */
  const heartbeatM = useMutation({
    mutationFn: () => unitHeartbeat(id),
    onSuccess: () => toast.success("Heartbeat sent"),
    onError: (e: any) => toast.error(e?.message || "Heartbeat failed"),
  });

  const permitM = useMutation({
    mutationFn: () => permitCharging(id),
    onSuccess: () => toast.success("Charging permitted"),
    onError: (e: any) => toast.error(e?.message || "Permit failed"),
  });

  // Stop / Resume the ACTIVE session. The response carries the orchestrator's
  // resulting start_gate — report THAT, never the requested action: a resume re-runs
  // the full permit pipeline, so it can legitimately come back blocked (a TOU window
  // closed, or the driver's card hold expired, while the session was paused).
  const manualStopM = useMutation({
    mutationFn: (stop: boolean) => (stop ? stopSessionCharging(id) : resumeSessionCharging(id)),
    onSuccess: (res, stop) => {
      if (stop) {
        toast.success("Charging stopped");
      } else if (res.permitted) {
        toast.success("Charging resumed");
      } else {
        toast.warning(`Resumed, but charging is not permitted (${res.start_gate})`);
      }
      unitQ.refetch();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to change charging"),
  });

  // All single-field settings saves go through PATCH /api/unit/<id>/settings
  // — one route, one cmd:settings_update envelope per save, no cross-stream
  // ordering race (P0-3 fix). Per-field mutations below keep their toast
  // text so the UX is unchanged; only the wire path was consolidated.
  const workModeM = useMutation({
    mutationFn: (mode: "plug-and-charge" | "commercial-public") =>
      patchUnitSettings(id, { work_mode: mode }),
    onSuccess: () => { toast.success("Work mode updated"); unitQ.refetch(); },
    onError: (e: any) => toast.error(e?.message || "Failed to update work mode"),
  });

  const selfServiceM = useMutation({
    mutationFn: (enabled: boolean) =>
      patchUnitSettings(id, { customer_self_service_enabled: enabled }),
    onSuccess: () => { toast.success("Self-service updated"); unitQ.refetch(); },
    onError: (e: any) => toast.error(e?.message || "Failed to update self-service"),
  });

  const controlM = useMutation({
    mutationFn: (patch: { name?: string; online_current?: number | null }) =>
      patchUnitSettings(id, {
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.online_current != null
          ? { online_current_limit: patch.online_current }
          : {}),
      }),
    onSuccess: () => { toast.success("Updated"); unitQ.refetch(); },
    onError: (e: any) => toast.error(e?.message || "Control update failed"),
  });

  /* ---- Local state ---- */
  const [unitName, setUnitName] = React.useState("");
  const [currentLimit, setCurrentLimit] = React.useState("");

  React.useEffect(() => {
    if (unitQ.data?.name) setUnitName(unitQ.data.name);
    if (unitQ.data?.online_current_limit !== undefined && unitQ.data?.online_current_limit !== null) {
      setCurrentLimit(String(unitQ.data.online_current_limit));
    }
  }, [unitQ.data]);

  const u = unitQ.data;
  // Only false when the server explicitly says the live telemetry mirror is
  // gone (offline > TTL); absent/undefined (legacy responses) counts as OK.
  const telemetryOk = u?.telemetry_available !== false;

  if (unitQ.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <span className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (unitQ.isError) {
    return (
      <div className="p-5">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">Failed to load unit</div>
      </div>
    );
  }

  const isCharging = (u?.state_name || "").toLowerCase() === "charging";
  const isOnline = u?.is_online ?? false;

  // ── Manual stop/resume ──────────────────────────────────────────────────────
  // Pure projection of backend state: whether a session is live, and what the PDP's
  // gate verdict currently is. The page never infers charging permission —
  // `stoppedByUser` is read from the gate, never from work_mode, tariff, current or
  // local optimistic state. Any work mode may be paused (paid/public included); the
  // orchestrator refuses what it cannot serve and the response says why.
  const session = u?.current_session;
  const sessionActive =
    !!session?.session_id &&
    session?.is_active !== false &&
    String(session?.session_ended ?? "") !== "1";
  const stoppedByUser = session?.start_gate === "stopped_by_user";
  const canManualStop = sessionActive;

  return (
    <div className="space-y-5 pt-[86px]">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" className="gap-1 mb-2" asChild>
            <Link to={backTo}><ArrowLeft className="h-4 w-4" /> {backLabel}</Link>
          </Button>
          <h1 className="text-2xl font-semibold">{u?.name || id}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs opacity-60">{id}</span>
            {isOnline ? (
              <Badge variant="success"><Wifi className="h-3 w-3 mr-1" /> Online</Badge>
            ) : (
              <Badge variant="destructive"><WifiOff className="h-3 w-3 mr-1" /> Offline</Badge>
            )}
            {u?.state_name && <Badge variant={isCharging ? "success" : "default"}>{u.state_name}</Badge>}
            {/* The session's gate verdict, projected. Shown whenever the PDP says
                the session is stopped, so the operator can tell "paused by us"
                apart from "the car finished". */}
            {stoppedByUser && (
              <Badge variant="destructive" className="gap-1">
                <Pause className="h-3 w-3" /> Charging stopped
              </Badge>
            )}
            {u?.work_mode === "commercial-public" && (
              <span className="inline-flex items-center rounded-full bg-indigo-100 text-indigo-700 px-2.5 py-0.5 text-xs font-medium">Commercial Public</span>
            )}
            {u?.organization && (
              // Clickable for roles that can open /organizations/:orgId
              // (RequireAdminOrOrg); plain badge for everyone else.
              (isAdmin || isOrgUser) ? (
                <Link to={`/organizations/${u.organization.id}`} title="Open organization">
                  <Badge variant="default" className="gap-1 cursor-pointer hover:opacity-80">
                    <Building className="h-3 w-3" /> {u.organization.name}
                  </Badge>
                </Link>
              ) : (
                <Badge variant="default" className="gap-1"><Building className="h-3 w-3" /> {u.organization.name}</Badge>
              )
            )}
            {u?.load_balancing_group && (
              // Clickable for roles that can open /load-balancing (RequireAdminOrOrg);
              // deep-links to this group's card via ?group=<id>. Plain badge otherwise.
              (isAdmin || isOrgUser) ? (
                <Link
                  to={`/load-balancing?group=${encodeURIComponent(u.load_balancing_group.group_id)}`}
                  title="Open load balancing group"
                >
                  <Badge variant="info" className="gap-1 cursor-pointer hover:opacity-80">
                    <SlidersHorizontal className="h-3 w-3" /> {u.load_balancing_group.name || u.load_balancing_group.group_id}
                  </Badge>
                </Link>
              ) : (
                <Badge variant="info" className="gap-1">
                  <SlidersHorizontal className="h-3 w-3" /> {u.load_balancing_group.name || u.load_balancing_group.group_id}
                </Badge>
              )
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Message-log stream for THIS unit (the Logs page, pre-filtered) —
              admin-gated because /logs is RequireAdmin. */}
          {isAdmin && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/logs?unit_id=${encodeURIComponent(id ?? "")}`}>
                <ScrollText className="h-4 w-4 mr-1" /> Logs
              </Link>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => heartbeatM.mutate()} disabled={heartbeatM.isPending}>
            <Send className="h-4 w-4 mr-1" /> Heartbeat
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => permitM.mutate()} disabled={permitM.isPending}>
              <Zap className="h-4 w-4 mr-1" /> Permit Charging
            </Button>
          )}
          {/* Stop / Resume the active session — only while one is live, since there
              is nothing to pause otherwise. The 0 A comes from the orchestrator's
              gate verdict, never from this button. */}
          {canManualStop && (
            stoppedByUser ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => manualStopM.mutate(false)}
                disabled={manualStopM.isPending}
              >
                <Play className="h-4 w-4 mr-1" /> Resume Charging
              </Button>
            ) : (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => manualStopM.mutate(true)}
                disabled={manualStopM.isPending}
              >
                <Pause className="h-4 w-4 mr-1" /> Stop Charging
              </Button>
            )
          )}
        </div>
      </div>

      {/* Tabs. A lone "Overview" tab is not a choice, so non-admins get no
          tab bar at all rather than a one-item one. */}
      {canReadTelemetry && (
      <div className="flex gap-1 border-b border-border" role="tablist">
        {([
          { id: "overview", label: "Overview" },
          { id: "telemetry", label: "Telemetry history" },
        ] as const).map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            data-testid={`unit-tab-${item.id}`}
            onClick={() =>
              setSearchParams(
                (current) => {
                  const next = new URLSearchParams(current);
                  if (item.id === "overview") next.delete("tab");
                  else next.set("tab", item.id);
                  return next;
                },
                { replace: true },
              )
            }
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              tab === item.id
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
      )}

      {tab === "telemetry" ? (
        <UnitTelemetryPanel unitId={id} />
      ) : (
      <>
      {/* Location */}
      {(u?.station_name || u?.station_address || u?.location) && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Location</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                {u.station_name && (
                  <div className="text-base font-semibold">{u.station_name}</div>
                )}
                {u.station_address && (
                  <div className="text-sm opacity-70 mt-0.5">{u.station_address}</div>
                )}
                {!u.station_name && !u.station_address && u.location && (
                  <div className="text-sm opacity-70">{u.location}</div>
                )}
                {u.station_time_zone && (
                  <div className="text-xs opacity-50 mt-1">Timezone: {u.station_time_zone}</div>
                )}
              </div>
              {u.station_latitude != null && u.station_longitude != null && (
                <div>
                  <div className="text-xs opacity-60">Coordinates</div>
                  <div className="font-mono text-sm mt-1">
                    {u.station_latitude.toFixed(6)}, {u.station_longitude.toFixed(6)}
                  </div>
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${u.station_latitude}&mlon=${u.station_longitude}&zoom=18`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary hover:underline mt-1 inline-block"
                  >
                    View on map →
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Telemetry */}
      <Card>
        <CardHeader><CardTitle>Live Telemetry</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3">
            {/* telemetry_available=false → the telemetry mirror expired (unit
                offline): the numeric fields are placeholder zeros, not real
                readings — render "—" instead of a misleading 0.0 V. */}
            <TelemetryItem label="Voltage" value={telemetryOk ? u?.voltage : null} unit="V" decimals={1} />
            <TelemetryItem label="Current" value={telemetryOk ? u?.current : null} unit="A" decimals={1} />
            <TelemetryItem label="Power" value={telemetryOk ? u?.power_kw : null} unit="kW" decimals={2} />
            <TelemetryItem label="Frequency" value={telemetryOk ? u?.frequency : null} unit="Hz" decimals={1} />
            <TelemetryItem label="Power Factor" value={telemetryOk ? u?.power_factor : null} unit="" decimals={2} />
            <TelemetryItem label="Session Energy" value={telemetryOk && u?.session_energy ? u.session_energy / 1000 : null} unit="kWh" decimals={2} icon={<Zap className="h-4 w-4 text-success" />} />
            <TelemetryItem label="Lifetime Energy" value={telemetryOk && u?.lifetime_energy ? u.lifetime_energy / 1000 : null} unit="kWh" decimals={1} icon={<Zap className="h-4 w-4 text-info" />} />
            <TelemetryItem label="Temperature" value={telemetryOk ? u?.temperature : null} unit="°C" decimals={0} icon={<Thermometer className="h-4 w-4 text-warning" />} />
            <TelemetryItem label="WiFi RSSI" value={telemetryOk ? u?.wifi_rssi : null} unit="dBm" decimals={0} icon={<Radio className="h-4 w-4 text-info" />} />
            <div className="rounded-xl border border-border p-3">
              <div className="text-xs opacity-60">IP</div>
              <div className="mt-1 font-mono text-xs">{u?.ip_address || "—"}</div>
              {u?.location && <div className="mt-0.5 text-xs opacity-50 truncate">{u.location}</div>}
              {u?.location_isp && <div className="mt-0.5 text-xs opacity-40 truncate">{u.location_isp}</div>}
            </div>
            <div className="rounded-xl border border-border p-3">
              <div className="text-xs opacity-60">MAC</div>
              <div className="mt-1 font-mono text-xs">{u?.mac_address || "—"}</div>
            </div>
            <div className="rounded-xl border border-base-200 p-3">
              <div className="text-xs opacity-60">Last Seen</div>
              <div className="mt-1 font-semibold text-sm">{formatRelativeTime(u?.last_seen)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Work Mode + Tariff (admin or org member) */}
        {(isAdmin || isOrgUser) && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="h-4 w-4" /> Work Mode</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <ToggleGroup
              type="single"
              size="sm"
              variant="outline"
              value={u?.work_mode ?? "plug-and-charge"}
              onValueChange={(v) => v && workModeM.mutate(v as "plug-and-charge" | "commercial-public")}
            >
              <ToggleGroupItem value="plug-and-charge" disabled={workModeM.isPending}>Plug & Charge</ToggleGroupItem>
              <ToggleGroupItem value="commercial-public" disabled={workModeM.isPending}>Commercial Public</ToggleGroupItem>
            </ToggleGroup>
            {u?.work_mode === "commercial-public" && (
              <>
                <p className="text-xs text-muted-foreground -mb-2">
                  <strong>Default rate</strong> — used when no eligible pricing profile matches the driver.
                </p>
                <TariffEditor unitId={id} onSuccess={() => unitQ.refetch()} inline />
                <AppliedPricingProfiles unitId={id} />
                <div className="border-t pt-6 mt-2">
                  <div className="flex items-center justify-between gap-4">
                    <Label>Customer Self-Service</Label>
                    <Switch
                      checked={u?.customer_self_service_enabled ?? true}
                      onCheckedChange={(val) => selfServiceM.mutate(val)}
                      disabled={selfServiceM.isPending}
                    />
                  </div>
                  {(u?.customer_self_service_enabled ?? true) && (
                    <>
                      <div className="mt-2 text-xs opacity-60">
                        Customers can use the charging site at <span className="font-mono">{window.location.origin}/charge/{encodeURIComponent(id)}</span>
                      </div>
                      <PaymentQrCode
                        url={`${window.location.origin}/charge/${encodeURIComponent(id)}`}
                        unitName={u?.name || id}
                      />
                    </>
                  )}
                  {!(u?.customer_self_service_enabled ?? true) && (
                    <div className="mt-2 text-xs opacity-60">
                      Disabled — admin controls charging
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Unit Control */}
        <Card>
          <CardHeader><CardTitle>Unit Control</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <Label>Unit Name</Label>
                {u?.config_available === false ? (
                  <div className="mt-1 text-sm">
                    <span className="text-red-600 font-medium">Editing disabled</span>{" "}
                    <span className="text-xs opacity-50">
                      — configuration unavailable (config mirror missing); saving could overwrite stored settings
                    </span>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-1">
                    <Input value={unitName} onChange={(e) => setUnitName(e.target.value)} />
                    <Button
                      variant="outline"
                      onClick={() => controlM.mutate({ name: unitName })}
                      disabled={controlM.isPending}
                    >
                      Save
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <Label>Online Current Limit (A)</Label>
                {u?.config_available === false ? (
                  <div className="mt-1 text-sm">
                    <span className="text-red-600 font-medium">No data</span>{" "}
                    <span className="text-xs opacity-50">
                      — configuration unavailable (config mirror missing; not editable)
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="number"
                        min={6}
                        max={u?.unit_rating ?? undefined}
                        value={currentLimit}
                        onChange={(e) => setCurrentLimit(e.target.value)}
                      />
                      <Button
                        variant="outline"
                        onClick={() => controlM.mutate({ online_current: parseFloat(currentLimit) || null })}
                        disabled={controlM.isPending}
                      >
                        Save
                      </Button>
                    </div>
                    {u?.unit_rating && (
                      <p className="text-xs text-primary mt-1">6A to {u.unit_rating}A</p>
                    )}
                  </>
                )}
              </div>
              {u?.tou_enabled && u?.effective_current_limit !== undefined && u.effective_current_limit !== null
                && u.effective_current_limit < (u?.online_current_limit ?? Infinity) && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                  {u.effective_current_limit === 0
                    ? 'TOU schedule is currently blocking charging (0A effective). Charging will resume when the schedule window starts.'
                    : `TOU schedule is limiting current to ${u.effective_current_limit}A (setting: ${u?.online_current_limit}A).`}
                </div>
              )}
              {isAdmin && u?.config_available !== false && u?.offline_current_limit != null && (
                <div>
                  <Label>Offline Current Limit (A)</Label>
                  <div className="mt-1 text-sm font-mono opacity-70">{u.offline_current_limit}A <span className="text-xs opacity-50">— internal parameter</span></div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* TOU (includes the ToU timezone, which anchors the schedule windows) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TouEditor
          key={id}
          unitId={id}
          touEnabled={(u as any)?.tou_enabled ?? false}
          touSchedules={(u as any)?.tou_schedules ?? []}
          timezone={u?.timezone ?? ""}
          configUnavailable={u?.config_available === false}
          onSuccess={() => unitQ.refetch()}
        />
      </div>

      {/* Sessions */}
      <SessionHistory unitId={id} showCosts={isAdmin || u?.work_mode === "commercial-public"} />

      {/* Billing (admin only) */}
      {isAdmin && (
        <Card>
          <CardHeader><CardTitle>Billing Summary</CardTitle></CardHeader>
          <CardContent>
            {billingQ.isLoading ? (
              <div className="text-sm opacity-60">Loading...</div>
            ) : billingQ.isError ? (
              <div className="text-sm text-error">Failed to load billing</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-xl border border-border p-3">
                  <div className="text-xs opacity-60">Finalized Revenue</div>
                  <div className="text-xl font-semibold">${(billingQ.data?.finalized_revenue ?? 0).toFixed(2)}</div>
                </div>
                <div className="rounded-xl border border-border p-3">
                  <div className="text-xs opacity-60">Active (est.)</div>
                  <div className="text-xl font-semibold">${(billingQ.data?.active_estimated_revenue ?? 0).toFixed(2)}</div>
                </div>
                <div className="rounded-xl border border-border p-3">
                  <div className="text-xs opacity-60">Total Sessions</div>
                  <div className="text-xl font-semibold">{billingQ.data?.total_sessions ?? 0}</div>
                </div>
                <div className="rounded-xl border border-border p-3">
                  <div className="text-xs opacity-60">Total Energy</div>
                  <div className="text-xl font-semibold">{(billingQ.data?.total_energy_kwh ?? 0).toFixed(2)} kWh</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Assigned Users (admin only) */}
      {isAdmin && <UnitAssignment unitId={id} />}
      </>
      )}
    </div>
  );
}

/* ---- Telemetry Item ---- */
function TelemetryItem({
  label,
  value,
  unit,
  decimals = 1,
  icon,
}: {
  label: string;
  value: number | null | undefined;
  unit: string;
  decimals?: number;
  icon?: React.ReactNode;
}) {
  const display = value !== null && value !== undefined && isFinite(value)
    ? `${value.toFixed(decimals)} ${unit}`
    : "—";

  return (
    <div className="rounded-xl border border-border p-3">
      <div className="flex items-center gap-1 text-xs opacity-60">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-semibold">{display}</div>
    </div>
  );
}
